import React, { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { useTutorial } from "./TutorialContext";
import { useTutorialRefs } from "./useTutorialRefs";
import TutorialTooltip from "./TutorialTooltip";

const PADDING = 8;
const SCROLL_OFFSET = 600;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const emptyRect: Rect = { top: 0, left: 0, width: 0, height: 0 };

export const TutorialOverlay: React.FC = () => {
  const { isOpen, currentStepIndex, steps } = useTutorial();
  const { getElementByStep } = useTutorialRefs();
  const [targetRect, setTargetRect] = useState<Rect>(emptyRect);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const currentStep = steps[currentStepIndex];

  const updateTargetRect = useCallback(() => {
    if (!isOpen || !currentStep) {
      setTargetRect(emptyRect);
      return;
    }

    const el = getElementByStep(
      currentStep.targetSelector,
      currentStep.targetRefKey
    );

    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      });

      // Only scroll if the target is not fully visible in the viewport.
      // Otherwise, scrollIntoView can push a top-of-page element under a
      // fixed header and leave the spotlight misaligned.
      const fullyVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;
      if (!fullyVisible) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      setTargetRect({
        top: windowSize.height / 2 - 50,
        left: windowSize.width / 2 - 100,
        width: 200,
        height: 100,
      });
    }
  }, [isOpen, currentStep, getElementByStep, windowSize.height, windowSize.width]);

  useEffect(() => {
    if (!isOpen) return;
    updateTargetRect();
  }, [isOpen, currentStepIndex, updateTargetRect]);

  useLayoutEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(updateTargetRect, SCROLL_OFFSET);
    return () => clearTimeout(timer);
  }, [isOpen, currentStepIndex, updateTargetRect]);

  if (!isOpen || !currentStep) return null;

  const targetEl = getElementByStep(
    currentStep.targetSelector,
    currentStep.targetRefKey
  );
  const spotlightRadius = (() => {
    if (!targetEl) return 8;
    const style = window.getComputedStyle(targetEl);
    const r = parseFloat(style.borderRadius);
    return Number.isNaN(r) ? 8 : r + PADDING;
  })();

  return (
    <Box
      data-testid="tutorial-overlay"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        pointerEvents: "auto",
      }}
    >
      {/* Top dim */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, targetRect.top),
          bgcolor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      {/* Bottom dim */}
      <Box
        sx={{
          position: "absolute",
          top: targetRect.top + targetRect.height,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      {/* Left dim */}
      <Box
        sx={{
          position: "absolute",
          top: targetRect.top,
          left: 0,
          width: Math.max(0, targetRect.left),
          height: targetRect.height,
          bgcolor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      {/* Right dim */}
      <Box
        sx={{
          position: "absolute",
          top: targetRect.top,
          left: targetRect.left + targetRect.width,
          right: 0,
          height: targetRect.height,
          bgcolor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* Spotlight border */}
      <Box
        sx={{
          position: "absolute",
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
          borderRadius: `${spotlightRadius}px`,
          boxShadow: "0 0 0 4px #EE2D3D, 0 0 20px rgba(238, 45, 61, 0.4)",
          pointerEvents: "none",
        }}
      />

      <TutorialTooltip targetRect={targetRect} />
    </Box>
  );
};

export default TutorialOverlay;
